import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_URL, ApiError, apiFetch } from './api.js';
import { signInWithPasskey } from './passkeys.js';
import { RECIPE_IMAGE_CACHE } from './pwa/recipeImageCache.js';
import { openExport } from './crypto/exportOpen.js';
import { clearLocalData } from './db/local.js';
import { resetHouseholdKeyTimer } from './crypto/householdKeys.js';

export interface SessionUser {
  id: string;
  email: string;
  name: string | null;
  /** E16: Kinder sehen kein Training, keine Ernährung, keine Kosten. Fehlt = erwachsen. */
  role?: 'adult' | 'child';
}

export const SESSION_QUERY_KEY = ['session'] as const;

/** Zuletzt bestätigtes Konto — kein Geheimnis, nur Kennung, Adresse, Name. */
const REMEMBERED_KEY = 'kraftwerk:session';

/**
 * Wem die Daten auf diesem Gerät gehören. Überlebt eine abgelaufene Sitzung
 * (dann bleiben nicht gesendete Änderungen liegen), nicht aber das Abmelden.
 * Meldet sich danach ein **anderes** Konto an, wird das Gerät vorher geleert —
 * sonst landeten Alices Einträge in Bobs Konto.
 */
const OWNER_KEY = 'kraftwerk:owner';

function owner(): string | null {
  try {
    return localStorage.getItem(OWNER_KEY);
  } catch {
    return null;
  }
}

/**
 * Rolle des angemeldeten Kontos (E16) — aus dem zuletzt bestätigten Konto auf
 * dem Gerät, also auch offline und ohne Abfrage-Kontext.
 */
export function currentRole(): 'adult' | 'child' {
  return remembered()?.role === 'child' ? 'child' : 'adult';
}

/** Wem die Daten auf dem Gerät gehören — für Stellen ohne Zugriff auf die Sitzung. */
export function currentOwnerId(): string | null {
  return owner();
}

function setOwner(userId: string | null): void {
  try {
    if (userId) localStorage.setItem(OWNER_KEY, userId);
    else localStorage.removeItem(OWNER_KEY);
  } catch {
    // ohne Speicher kein Schutz vor Kontowechsel — dann leert eben das Abmelden
  }
}

function remember(user: SessionUser | null): void {
  if (user) setOwner(user.id);
  try {
    if (user) localStorage.setItem(REMEMBERED_KEY, JSON.stringify(user));
    else localStorage.removeItem(REMEMBERED_KEY);
  } catch {
    // Ohne Speicher gibt es eben keinen Offline-Start — kein Grund abzubrechen.
  }
}

function remembered(): SessionUser | null {
  try {
    const raw = localStorage.getItem(REMEMBERED_KEY);
    const user = raw ? (JSON.parse(raw) as Partial<SessionUser>) : null;
    return user && typeof user.id === 'string' && typeof user.email === 'string'
      ? {
          id: user.id,
          email: user.email,
          name: user.name ?? null,
          ...(user.role === 'child' ? { role: 'child' as const } : {}),
        }
      : null;
  } catch {
    return null;
  }
}

/**
 * Wer ist angemeldet? `null` heißt „niemand" — das ist ein normaler Zustand und
 * kein Fehler. Würde man 401 als Fehler behandeln, zeigte die App beim ersten
 * Aufruf eine Fehlermeldung statt des Anmeldeformulars.
 *
 * **Nur 401 heißt „abgemeldet".** Ist der Server nicht erreichbar (Funkloch im
 * Studio, Tunnel weg, Proxy-Fehler), läuft die App mit dem zuletzt bestätigten
 * Konto und den Daten auf dem Gerät weiter. Bis zum 11.10.2026 zeigte sie dann
 * das Anmeldeformular — eine Offline-App, die offline rauswirft.
 */
async function fetchSession(): Promise<SessionUser | null> {
  try {
    const body = await apiFetch<{ user: SessionUser }>('/auth/me');
    remember(body.user);
    return body.user;
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      remember(null);
      return null;
    }
    const known = remembered();
    if (known) return known;
    throw error;
  }
}

/** Sitzung abgelaufen: neu fragen — der Server sagt 401, das Formular erscheint. */
export function useSessionExpired(): () => void {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
}

export function useSession() {
  return useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: fetchSession,
    retry: false,
    staleTime: 60_000,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (credentials: { email: string; password: string }) =>
      apiFetch<{ user: SessionUser }>('/auth/login', {
        method: 'POST',
        body: JSON.stringify(credentials),
      }),
    onSuccess: (body) => signedIn(queryClient, body.user),
  });
}

/** Anmelden mit Face ID — ohne E-Mail, ohne Passwort. */
export function usePasskeyLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: signInWithPasskey,
    onSuccess: (body) => signedIn(queryClient, body.user),
  });
}

/** Kind (E16): mit dem Einmal-Code vom Elterngerät — kein Passwort, keine E-Mail. */
export function useChildCodeLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (code: string) =>
      apiFetch<{ user: SessionUser }>('/auth/child-code', {
        method: 'POST',
        body: JSON.stringify({ code }),
      }),
    onSuccess: (body) => signedIn(queryClient, body.user),
  });
}

/**
 * Nach jeder erfolgreichen Anmeldung, gleich auf welchem Weg. Anderes Konto
 * als zuletzt auf diesem Gerät: erst leeren, dann weiter.
 */
async function signedIn(
  queryClient: ReturnType<typeof useQueryClient>,
  user: SessionUser,
): Promise<void> {
  const previous = owner();
  if (previous && previous !== user.id) {
    await clearLocalData();
    await forgetImages();
  }
  remember(user);
  // Schlüssel gleich abgleichen, nicht erst nach der Fünf-Minuten-Sperre:
  // Ein Kind, das sich in derselben App neu anmeldet, wartete sonst so lange
  // auf seinen Haushaltsschlüssel (lokal getestet am 26.10.2026).
  resetHouseholdKeyTimer();
  queryClient.setQueryData(SESSION_QUERY_KEY, user);
}

/**
 * Alles verwerfen, nicht nur die Sitzung: nach dem Abmelden darf kein Datensatz
 * des vorigen Nutzers auf dem Gerät bleiben — weder im Zwischenspeicher noch in
 * der lokalen Datenbank. Sonst taucht er beim nächsten Anmelden im falschen
 * Konto wieder auf.
 */
/** Gespeicherte Rezeptbilder gehören dem vorigen Konto — sie gehen mit. */
async function forgetImages(): Promise<void> {
  await globalThis.caches?.delete(RECIPE_IMAGE_CACHE).catch(() => false);
}

async function forgetEverything(queryClient: ReturnType<typeof useQueryClient>): Promise<void> {
  await clearLocalData();
  await forgetImages();
  remember(null);
  setOwner(null);
  // Alles weg — außer der Sitzung selbst. `clear()` nahm sie mit: Die Anzeige
  // beobachtete danach einen Eintrag, den es nicht mehr gab, und eine erneute
  // Anmeldung blieb unsichtbar, bis man die App neu startete (iPhone, 12.10.2026).
  queryClient.removeQueries({
    predicate: (query) => query.queryKey[0] !== SESSION_QUERY_KEY[0],
  });
  queryClient.setQueryData(SESSION_QUERY_KEY, null);
}

export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await fetch(`${API_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    },
    onSettled: () => forgetEverything(queryClient),
  });
}

/**
 * Konto endgültig löschen. Erst wenn der Server bestätigt hat, wird auch das
 * Gerät geleert — scheitert es (falsches Passwort, kein Netz), bleibt alles.
 */
export function useDeleteAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (password: string) =>
      apiFetch<{ householdsHandedOver: number; sharedItemsKept: number }>('/account', {
        method: 'DELETE',
        body: JSON.stringify({ password }),
      }),
    onSuccess: () => forgetEverything(queryClient),
  });
}

/** Alle Daten des Kontos als Datei — über das Teilen-Menü, sonst als Download. */
export async function exportAccountData(): Promise<'shared' | 'downloaded' | 'cancelled'> {
  const response = await fetch(`${API_URL}/account/export`, { credentials: 'include' });
  if (!response.ok) throw new ApiError(`HTTP ${response.status}`, response.status);
  // Verschlüsseltes (E21) öffnet das Gerät vor dem Teilen.
  const opened = await openExport(await response.json());
  const blob = new Blob([JSON.stringify(opened, null, 2)], { type: 'application/json' });
  const day = new Date().toISOString().slice(0, 10);
  const file = new File([blob], `kraftwerk-export-${day}.json`, { type: 'application/json' });

  if (typeof navigator.canShare === 'function' && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file] });
      return 'shared';
    } catch {
      return 'cancelled';
    }
  }
  const url = URL.createObjectURL(file);
  const link = document.createElement('a');
  link.href = url;
  link.download = file.name;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return 'downloaded';
}
